import Cookies from 'js-cookie';
import { gql } from '@apollo/client';
import { addHours } from 'date-fns';
import { getClient } from './utils/apollo-client';
import warning from './utils/logger';

// Tokens expire after 12 hours. Using 11 to be on the safe side.
// https://developers.hubspot.com/docs/api/conversation/visitor-identification
const TOKEN_EXPIRES = 11; // hours
const CACHE_KEY_PREFIX = 'HVID'; // HubSpot Visitor Identification Token.

interface HubSpotConversationsWidget {
    load: () => void;
}

interface HubSpotConversations {
    widget: HubSpotConversationsWidget;
}

interface HubSpotConversationsSettings {
    identificationEmail: string;
    identificationToken: string;
}

declare global {
    interface Window {
        HubSpotConversations?: HubSpotConversations;
        hsConversationsOnReady?: Array<() => void>;
        hsConversationsSettings?: HubSpotConversationsSettings;
    }
}

function createExpireDate(): Date {
    return addHours(new Date(), TOKEN_EXPIRES);
}

function loadWidget(): void {
    function load(): void {
        return window.HubSpotConversations?.widget.load();
    }

    if (window.HubSpotConversations) {
        load();
    } else {
        // Snippet was not initialized yet. These callbacks will be
        // called once the external API has been initialized.
        window.hsConversationsOnReady = [load];
    }
}

const query = gql`
    query getHubSpotIdentificationToken {
        hubspotIdentificationToken
    }
`;

interface HubSpotTokenQueryResult {
    hubspotIdentificationToken: string;
}

async function requestToken(): Promise<string> {
    const result = await getClient().query<HubSpotTokenQueryResult>({
        query,
    });

    const token = result.data.hubspotIdentificationToken;

    if (!token) {
        throw new Error('No token in query result.');
    }

    return token;
}

async function getToken(userPk: number): Promise<string> {
    const cacheKey = `${CACHE_KEY_PREFIX}_${userPk}`;

    const tokenFromCache = Cookies.get(cacheKey);

    if (tokenFromCache) {
        return tokenFromCache;
    }

    const token = await requestToken();

    Cookies.set(cacheKey, token, { expires: createExpireDate() });

    return token;
}

async function tryToIdentify(userPk: number, email: string): Promise<void> {
    try {
        const token = await getToken(userPk);

        if (!token) {
            return;
        }

        // Set identification details.
        window.hsConversationsSettings = {
            identificationEmail: email,
            identificationToken: token,
        };
    } catch (error) {
        warning(`Failed to do HubSpot identification due to: ${String(error)}`);
    }
}

interface HubspotIdentifyModuleOptions {
    isAuthenticated: boolean;
    userPk: number;
    email: string;
}

async function hubspotIdentifyModule({
    options,
}: {
    options: HubspotIdentifyModuleOptions;
}): Promise<void> {
    if (options.isAuthenticated) {
        await tryToIdentify(options.userPk, options.email);
    }

    loadWidget();
}

export default hubspotIdentifyModule;
